import React, { useMemo } from 'react';
import { getRunDateString } from '../utils/dateUtils';

const WeeklyWidget = ({ runs, startDate, targetDate, targetKm }) => {
  const stats = useMemo(() => {
    const today = new Date();
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay() + 1);
    currentWeekStart.setHours(0, 0, 0, 0);

    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    currentWeekEnd.setHours(23, 59, 59, 999);

    const weekRuns = runs.filter(run => {
      const runDate = new Date(getRunDateString(run.date));
      return runDate >= currentWeekStart && runDate <= currentWeekEnd;
    });

    const actualKmThisWeek = weekRuns.reduce((sum, run) => sum + Number(run.kilometers), 0);

    const totalDays = (targetDate - startDate) / (1000 * 60 * 60 * 24);
    const kmPerDay = targetKm / totalDays;
    const expectedKmPerWeek = kmPerDay * 7;

    const daysSinceMonday = Math.min(
      Math.ceil((today - currentWeekStart) / (1000 * 60 * 60 * 24)),
      7
    );
    const expectedKmSoFarThisWeek = kmPerDay * daysSinceMonday;

    const performancePercentage = expectedKmSoFarThisWeek > 0
      ? (actualKmThisWeek / expectedKmSoFarThisWeek) * 100
      : 0;

    const isAhead = actualKmThisWeek >= expectedKmSoFarThisWeek;

    return {
      actualKm: actualKmThisWeek,
      expectedKm: expectedKmSoFarThisWeek,
      expectedKmFullWeek: expectedKmPerWeek,
      difference: actualKmThisWeek - expectedKmSoFarThisWeek,
      isAhead,
      performancePercentage,
      runsCount: weekRuns.length,
      daysSinceMonday: Math.ceil(daysSinceMonday)
    };
  }, [runs, startDate, targetDate, targetKm]);

  return (
    <div
      className="weekly-progress-widget"
      style={{
        backgroundColor: stats.isAhead ? '#d4edda' : '#f8d7da',
        borderLeft: `5px solid ${stats.isAhead ? '#28a745' : '#dc3545'}`,
        padding: '20px',
        margin: '20px auto',
        maxWidth: '800px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <h2 style={{
        margin: '0 0 15px 0',
        color: stats.isAhead ? '#155724' : '#721c24',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        {stats.isAhead ? '✓' : '⚠'} Diese Woche
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
        <div className="week-stat">
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: stats.isAhead ? '#28a745' : '#dc3545' }}>
            {stats.actualKm.toFixed(1)} km
          </div>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>Ist-Kilometer</div>
        </div>

        <div className="week-stat">
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#666' }}>
            {stats.runsCount > 0 ? (stats.actualKm / stats.runsCount).toFixed(1) : '0.0'} km
          </div>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>Ø pro Lauf</div>
        </div>

        <div className="week-stat">
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#666' }}>
            {(stats.expectedKmFullWeek - stats.actualKm).toFixed(1)} km
          </div>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>Noch zu erbringen</div>
        </div>

        <div className="week-stat">
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: stats.performancePercentage >= 100 ? '#28a745' : '#dc3545' }}>
            {stats.performancePercentage.toFixed(0)}%
          </div>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>Performance</div>
        </div>
      </div>

      <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid rgba(0,0,0,0.1)', fontSize: '0.9rem', color: '#666' }}>
        <strong>{stats.runsCount}</strong> Läufe diese Woche
        {' • '}
        <strong>{stats.daysSinceMonday}</strong> von 7 Tagen vergangen
        {' • '}
        Wochenziel: <strong>{stats.expectedKmFullWeek.toFixed(1)} km</strong>
      </div>
    </div>
  );
};

export default WeeklyWidget;
